/**
 * SubscriptionPicker (T35.7.2 Part A) — the reusable subscription amount picker.
 *
 * Owned by the subscriptions module. Idea/donation-agnostic — takes a confirm
 * label + `onConfirm(amountCents, recurring)`. The header copy and framing are
 * the caller's responsibility (`BackIdeaDialog`, `DonationDialog`).
 *
 * The ladder is $1 / $3 / $5 / $10 / $20 / Custom; the recurring toggle defaults
 * ON (weekly / biweekly / monthly); "Confirm" stays disabled until a positive
 * amount is chosen.
 *
 * Token-only (CP5); mobile-first; ≥44px targets.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Spinner } from '../../components/Spinner';
import type { RecurringSchedule } from './api';

export const LADDER = [1, 3, 5, 10, 20] as const;

export const RECURRING_OPTIONS: Array<{ value: Exclude<RecurringSchedule, 'none'>; label: string }> = [
  { value: 'weekly', label: 'weekly' },
  { value: 'biweekly', label: 'biweekly' },
  { value: 'monthly', label: 'monthly' },
];

/** The confirmed-state suffix for an amount (matches the proto's FREQ_LBL + " once"). */
export function subscriptionSuffix(recurring: RecurringSchedule): string {
  switch (recurring) {
    case 'weekly':
      return '/week';
    case 'biweekly':
      return '/2 weeks';
    case 'monthly':
      return '/month';
    default:
      return ' once';
  }
}

const amtsStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  marginBottom: 'var(--space-3)',
};

function amtBtnStyle(on: boolean): CSSProperties {
  const base: CSSProperties = {
    flex: '1 0 4rem',
    minHeight: '2.75rem',
    borderWidth: '0.0625rem',
    borderStyle: 'solid',
    borderColor: 'var(--color-border-strong)',
    background: 'var(--color-bg-primary)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-bold)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };
  return on
    ? { ...base, borderColor: 'var(--color-accent-primary)', background: 'var(--color-accent-subtle)', color: 'var(--color-accent-primary)' }
    : base;
}

const customWrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  marginBottom: 'var(--space-3)',
  padding: 'var(--space-2) var(--space-3)',
  border: '0.0625rem solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-primary)',
};

const customInputStyle: CSSProperties = {
  flex: 1,
  border: 0,
  font: 'inherit',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
  background: 'transparent',
  outline: 'none',
  minWidth: 0,
};

const recBoxStyle: CSSProperties = {
  marginBottom: 'var(--space-4)',
  padding: 'var(--space-3)',
  border: '0.0625rem solid var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-raised)',
};

const recToggleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: '2.75rem',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

const recLineStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const freqSelStyle: CSSProperties = {
  font: 'inherit',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-bold)',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
};

const ghostBtnStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: '0.0625rem solid var(--color-border-strong)',
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-secondary)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

function confirmBtnStyle(disabled: boolean): CSSProperties {
  return {
    flex: 1,
    minHeight: '2.75rem',
    border: 0,
    background: disabled ? 'var(--color-bg-muted)' : 'var(--color-accent-primary)',
    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-bold)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-family-sans)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-2)',
  };
}

export function SubscriptionPicker({
  open,
  confirmLabel,
  busy = false,
  onClose,
  onConfirm,
}: {
  /** When true the picker is in its active/open state (resets internal state on transition). */
  open: boolean;
  /** Label for the confirm button (e.g. "Back it", "Support"). */
  confirmLabel: string;
  busy?: boolean;
  onClose: () => void;
  /** Confirm with a positive whole-dollar amount × 100 + the recurring schedule. */
  onConfirm: (amountCents: number, recurring: RecurringSchedule) => void;
}): JSX.Element {
  const [amount, setAmount] = useState<number | null>(null);
  const [custom, setCustom] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  const [recurringOn, setRecurringOn] = useState(true);
  const [freq, setFreq] = useState<Exclude<RecurringSchedule, 'none'>>('weekly');

  // Reset each time the picker opens (recurring defaults ON, weekly).
  useEffect(() => {
    if (open) {
      setAmount(null);
      setCustom('');
      setCustomOpen(false);
      setRecurringOn(true);
      setFreq('weekly');
    }
  }, [open]);

  const effectiveAmount = customOpen ? Number.parseInt(custom, 10) : amount;
  const valid = Number.isFinite(effectiveAmount) && (effectiveAmount as number) > 0;
  const recurring: RecurringSchedule = recurringOn ? freq : 'none';

  function pickLadder(val: number): void {
    setCustomOpen(false);
    setCustom('');
    setAmount(val);
  }
  function pickCustom(): void {
    setCustomOpen(true);
    setAmount(null);
  }
  function confirm(): void {
    if (!valid || busy) return;
    onConfirm((effectiveAmount as number) * 100, recurring);
  }

  return (
    <div data-testid="subscription-picker">
      <div style={amtsStyle} role="group" aria-label="Subscription amount">
        {LADDER.map((v) => (
          <button
            key={v}
            type="button"
            style={amtBtnStyle(!customOpen && amount === v)}
            aria-pressed={!customOpen && amount === v}
            data-testid={`sub-amt-${v}`}
            onClick={() => pickLadder(v)}
          >
            ${v}
          </button>
        ))}
        <button
          type="button"
          style={amtBtnStyle(customOpen)}
          aria-pressed={customOpen}
          data-testid="sub-amt-custom"
          onClick={pickCustom}
        >
          Custom
        </button>
      </div>

      {customOpen && (
        <div style={customWrapStyle}>
          <span aria-hidden="true" style={{ color: 'var(--color-text-muted)' }}>
            $
          </span>
          <input
            type="number"
            min={1}
            inputMode="numeric"
            aria-label="Custom subscription amount in dollars"
            placeholder="Enter an amount"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            style={customInputStyle}
            data-testid="sub-custom-input"
          />
        </div>
      )}

      <div style={recBoxStyle}>
        <label style={recToggleStyle}>
          <input
            type="checkbox"
            className="rec-cb"
            checked={recurringOn}
            onChange={(e) => setRecurringOn(e.target.checked)}
            data-testid="sub-recurring"
          />
          <span>Repeat automatically</span>
        </label>
        <div style={recLineStyle} data-testid="sub-recurring-line">
          {recurringOn ? (
            <>
              Repeats{' '}
              <select
                style={freqSelStyle}
                aria-label="How often it repeats"
                value={freq}
                onChange={(e) => setFreq(e.target.value as Exclude<RecurringSchedule, 'none'>)}
                data-testid="sub-freq"
              >
                {RECURRING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>{' '}
              — change or stop anytime.
            </>
          ) : (
            'Backing just once — tick the box to repeat automatically.'
          )}
        </div>
      </div>

      <div style={actionsStyle}>
        <button type="button" style={ghostBtnStyle} onClick={onClose} data-testid="sub-cancel">
          Not now
        </button>
        <button
          type="button"
          style={confirmBtnStyle(!valid || busy)}
          disabled={!valid || busy}
          aria-busy={busy || undefined}
          onClick={confirm}
          data-testid="sub-confirm"
        >
          {busy && (
            <Spinner
              size="sm"
              color="var(--color-text-inverse)"
              label="Processing"
              data-testid="sub-confirm-spinner"
            />
          )}
          {/* Keep the label visible alongside the spinner as a static text cue
              (it doesn't change to "Processing…" — the spinner conveys the wait). */}
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
